import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  updateInfo as updateInfoInRad,
  addRole as addRoleToRad,
  updateRole as updateRoleInRad,
  updateThread as updateThreadInRad,
  updateActivity as updateActivityInRad,
  updateStateline as updateStatelineInRad,
  updatePosition as updatePositionInRad,
  addActivity as addActivityToRad,
  addSubthread as addSubthreadToRad,
  deleteElement as deleteElementFromRad,
  detachSubthread as detachSubthreadFromRad,
  attachSubthread as attachSubthreadInRad,
  moveSubthread as moveSubthreadInRad,
  loopSubthread as loopSubthreadInRad,
  mergeStates as mergeStatesInRad,
  toggleShowLoop as toggleShowLoopInRad,
  deleteInteraction as deleteInteractionFromRad,
  deleteInteractionSegment as deleteInteractionSegmentFromRad,
  addInteraction as addInteractionToRad,
  unmergeThread as unmergeThreadInRad,
} from "@cadenza/rad-domain";
import {
  enforceVnextRequiredArgs,
  inferActivityContract,
} from "./contractValidation.js";
import { normalizeActivityForDomain } from "../activityKind.js";
//import { moveItem } from "@cadenza/utils";

export const RADDispatchContext = createContext();

export const RADDispatchProvider = ({ dispatch, state, children }) => {
  // Use a ref to track current state without causing useCallback dependencies to change
  const stateRef = useRef(state);

  // Keep the ref up to date
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const dispatchIfValid = useCallback(
    (action) => {
      if (action) {
        dispatch(action);
      } else {
        console.error("Failed to dispatch action due to invalid payload.");
      }
    },
    [dispatch]
  );

  const toggleShowLoop = ({ id }) => {
    if (!id) {
      console.warn("Invalid subthread ID for toggling showLoop:", id);
      return {
        success: false,
        message: "Invalid parameters: subthread_id is required",
      };
    }
    const result = toggleShowLoopInRad(state, { id });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to toggle showLoop:", result.message);
      return result;
    }
  };

  const moveSubthread = ({ subthread_id, direction = "" }) => {
    if (!subthread_id) {
      console.warn("Invalid subthread ID for movement:", subthread_id);
      return {
        success: false,
        message: "Invalid parameters: subthread.id is required",
      };
    }
    if (!["left", "right"].includes(direction)) {
      console.warn("Invalid direction for subthread movement:", direction);
      return {
        success: false,
        message: "Invalid parameters: direction must be 'left' or 'right'",
      };
    }

    const result = moveSubthreadInRad(state, { id: subthread_id, direction });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to move subthread:", result.message);
      return result;
    }
  };

  const addLoop = ([subthread, targets, target_state]) => {
    if (!subthread.id || !targets || !target_state.id) {
      console.warn("Invalid parameters for looping subthread:", {
        subthread_id: subthread.id,
        targets,
        target_state_id: target_state.id,
      });
      return {
        success: false,
        message:
          "Invalid parameters: subthread, targets, and target_state are required",
      };
    }
    const result = loopSubthreadInRad(state, {
      subthread_id: subthread.id,
      target_state_id: target_state.id,
    });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to loop subthread:", result.message);
      return result;
    }
  };

  const addMerge = ([source_thread, target_state]) => {
    if (!source_thread.id || !target_state.id) {
      console.warn("Invalid parameters for merging states:", {
        source_state_id: source_thread.poststate_id,
        target_state_id: target_state.id,
      });
      return {
        success: false,
        message:
          "Invalid parameters: source_state and target_state are required",
      };
    }
    const result = mergeStatesInRad(state, {
      source_state_id: source_thread.poststate_id,
      target_state_id: target_state.id,
    });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to merge states:", result.message);
      return result;
    }
  };

  const detachSubthread = ({ subthread_id }) => {
    if (!subthread_id) {
      console.warn("Invalid subthread ID for detachment:", subthread_id);
      return {
        success: false,
        message: "Invalid parameters: subthread_id is required",
      };
    }

    const result = detachSubthreadFromRad(state, { id: subthread_id });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to detach subthread:", result.message);
      return result;
    }
  };

  const unmergeThread = ({ thread_id }) => {
    if (!thread_id) {
      console.warn("Invalid thread ID for unmerging:", thread_id);
      return {
        success: false,
        message: "Invalid parameters: thread_id is required",
      };
    }

    const result = unmergeThreadInRad(state, { thread_id });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to unmerge thread:", result.message);
      return result;
    }
  };

  const attachSubthread = ({ subthread_id }) => {
    if (!subthread_id) {
      console.warn("Invalid subthread ID for attachment:", subthread_id);
      return {
        success: false,
        message: "Invalid parameters: subthread_id is required",
      };
    }

    const result = attachSubthreadInRad(state, { id: subthread_id });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to attach subthread:", result.message);
      return result;
    }
  };

  // 🚀 Update RAD Info
  const updateRADInfo = (info) => {
    if (!info) {
      console.warn("Invalid RAD info update payload:", info);
      return {
        success: false,
        message: "Invalid parameters: info is required",
      };
    }
    const result = updateInfoInRad(state, info);
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.warn("Dispatcher - Failed to update RAD info:", result.message);
      return {
        success: false,
        message: "Failed to update RAD info",
      };
    }
  };

  const deleteElement = ({ id, class: className, type }) => {
    if (!id || !className) {
      console.warn("Invalid component ID for deletion:", id);
      return {
        success: false,
        message: "Invalid parameters: id and class name are required",
      };
    }
    try {
      enforceVnextRequiredArgs(state, "deleteEntity", { id, className });
    } catch (error) {
      return { success: false, message: error.message };
    }

    const result =
      type === "interaction" || className === "connection"
        ? deleteInteractionFromRad(state, {
            component: { id, class: className, type },
          })
        : deleteElementFromRad(state, { id, className, type });

    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to delete element:", result.message);
      return result;
    }
  };

  // 🚀 Add Role
  const addRole = ({ position, ...options } = {}) => {
    if (
      !position ||
      typeof position.x !== "number" ||
      typeof position.y !== "number"
    ) {
      return {
        success: false,
        message: "Invalid parameters: parent_id is required",
      };
    }
    try {
      enforceVnextRequiredArgs(state, "addRole", {
        label: options?.label,
      });
    } catch (error) {
      return { success: false, message: error.message };
    }

    const result = addRoleToRad(state, { position, ...options });

    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Dispatcher - Failed to add role:", result.message);
      return result;
    }
  };

  const updateRole = ({ id, label, transient, multiple, labelPosition }) => {
    if (!id || !label) {
      console.warn("Invalid role for update:", {
        id,
        label,
        transient,
        multiple,
        labelPosition,
      });
      return {
        success: false,
        message: "Invalid parameters: Role id and label are required",
      };
    }

    const payload = { id };
    if (label !== undefined) payload.label = label;
    if (transient !== undefined) payload.transient = transient;
    if (multiple !== undefined) payload.multiple = multiple;
    if (labelPosition !== undefined) payload.labelPosition = labelPosition;

    const result = updateRoleInRad(state, payload);
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
    } else {
      console.error("Failed to update role in dispatcher:", result.message);
      return { success: false, message: result.message };
    }
  };

  // 📐 Update a thread
  const updateThread = ({ id, label }) => {
    if (!id || !label) {
      console.warn("Invalid thread for update:", { id, label });
      return { success: false, message: "Thread id and label are required" };
    }

    const result = updateThreadInRad(state, { id, label });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to update thread:", result.message);
      return result;
    }
  };

  const addActivity = ({ activity, target }) => {
    if (!activity || !target) {
      console.warn("Invalid activity or target for addition:", {
        activity,
        target,
      });
      return { success: false, message: "Activity and target are required" };
    }
    const normalizedActivity = normalizeActivityForDomain(activity);
    try {
      const contract = inferActivityContract({ activity: normalizedActivity, target });
      enforceVnextRequiredArgs(state, contract.op, contract.args);
    } catch (error) {
      return { success: false, message: error.message };
    }

    const result = addActivityToRad(state, { activity: normalizedActivity, target });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to add activity to RAD:", result.message);
      return result;
    }
  };

  // ⚖️ Add a subthread to a refinement
  const addSubthread = ({ parent_id, action, child_id }) => {
    if (!parent_id) {
      return {
        success: false,
        message: "Invalid parameters: parent_id is required",
      };
    }

    const result = addSubthreadToRad(state, {
      parent_id,
      action,
      child_id,
    });

    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to add subthread:", result.message);
      return result;
    }
  };

  // ✏️ Update Position
  const updatePosition = useCallback(
    ({ id, type, position = null }) => {
      if (!id || !type) {
        console.warn("Invalid position update payload:", {
          id,
          type,
          position,
        });
        return {
          success: false,
          message: "Position update requires id and type",
        };
      }

      const result = updatePositionInRad(stateRef.current, {
        id,
        type,
        ...position,
      });
      if (result.success) {
        dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
        return result;
      } else {
        console.error("Failed to update position:", result.message);
        return result;
      }
    },
    [dispatchIfValid]
  );

  // ✏️ Update Activity
  const updateActivity = (activity) => {
    if (!activity?.id) {
      console.warn("Invalid activity for update:", activity);
      return { success: false, message: "Activity with id is required" };
    }

    console.log("Updating activity in dispatcher:", activity);
    const result = updateActivityInRad(state, activity);
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to update activity:", result.message);
      return result;
    }
  };

  // ✏️ Update Stateline
  const updateStateline = ({ id, label, show }) => {
    if (!id) {
      console.warn("Invalid state for update:", { id, label, show });
      return { success: false, message: "State id is required" };
    }

    const result = updateStatelineInRad(state, { id, label, show });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to update state:", result.message);
      return result;
    }
  };

  // 🔄 Add Interaction.
  const addInteraction = ({ source, selected }) => {
    if (!source || !selected) {
      console.warn("Invalid interaction payload:", { source, selected });
      return { success: false, message: "Invalid interaction request" };
    }
    try {
      enforceVnextRequiredArgs(state, "addInteraction", {
        sourceActivityId: source?.id,
        targetActivityId: selected?.id,
      });
    } catch (error) {
      return { success: false, message: error.message };
    }
    console.log("Dispatcher Adding interaction between:", source, selected);
    const result = addInteractionToRad(state, { source, selected });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      return result;
    }
  };

  const deleteInteraction = ({ component }) => {
    if (!component?.id) {
      return { success: false, message: "Connection ID is required" };
    }
    const result = deleteInteractionFromRad(state, { component });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to delete interaction:", result.message);
      return result;
    }
  };

  const deleteInteractionSegment = ({ id, from, to }) => {
    // Deprecated
    if (!id || from === undefined || to === undefined) {
      console.warn("Invalid connection section deletion payload:", {
        id,
        from,
        to,
      });
      return {
        success: false,
        message: "Invalid connection segment deletion payload",
      };
    }
    const result = deleteInteractionSegmentFromRad(state, {
      id,
      from: from.id,
      to: to.id,
    });
    if (result.success) {
      dispatchIfValid({ type: "REPLACE_RAD", payload: result.data });
      return result;
    } else {
      console.error("Failed to delete connection segment:", result.message);
      return result;
    }
  };

  const context = {
    state,
    addInteraction,

    addRole,
    addActivity,
    addSubthread,
    updateRole,
    updateThread,
    updatePosition,
    updateActivity,
    updateStateline,
    deleteElement,
    moveSubthread,
    detachSubthread,
    attachSubthread,
    addLoop,
    addMerge,
    unmergeThread,
    toggleShowLoop,
    updateRADInfo,
    deleteInteraction,
    deleteInteractionSegment,
  };

  return (
    <RADDispatchContext.Provider value={context}>
      {children}
    </RADDispatchContext.Provider>
  );
};

export const useRADDispatch = () => {
  const context = useContext(RADDispatchContext);
  if (!context) {
    throw new Error("useRADDispatch must be used within a RADDispatchProvider");
  }
  return context;
};
